import React from 'react';
import axios from 'axios';
import { API } from '../App';
import { Button } from '@/components/ui/button';
import { X, Check, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

const NotificationPanel = ({ notifications, onClose, onRefresh }) => {
  const handleMarkRead = async (notificationId) => {
    try {
      await axios.post(`${API}/notifications/${notificationId}/mark-read`);
      onRefresh();
    } catch (error) {
      console.error('Failed to mark notification as read:', error);
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await axios.post(`${API}/notifications/mark-all-read`);
      toast.success('Alle kennisgewings gemerk as gelees');
      onRefresh();
    } catch (error) {
      toast.error('Kon nie kennisgewings opdateer nie');
    }
  };

  const handleClearAll = async () => {
    if (!confirm('Is jy seker jy wil alle kennisgewings permanent verwyder?')) {
      return;
    }
    
    try {
      await axios.delete(`${API}/notifications/clear-all`);
      toast.success('Alle kennisgewings verwyder');
      onRefresh();
    } catch (error) {
      toast.error('Kon nie kennisgewings verwyder nie');
      console.error('Clear notifications error:', error);
    }
  };

  const notificationTypeConfig = {
    info: { bg: 'bg-blue-50', border: 'border-blue-200', dot: 'bg-blue-500' },
    success: { bg: 'bg-green-50', border: 'border-green-200', dot: 'bg-green-500' },
    warning: { bg: 'bg-orange-50', border: 'border-orange-200', dot: 'bg-orange-500' },
    error: { bg: 'bg-red-50', border: 'border-red-200', dot: 'bg-red-500' },
  };

  return (
    <div className="absolute right-0 top-full mt-2 w-96 max-h-[500px] overflow-y-auto bg-white border-2 border-orange-200 rounded-lg shadow-xl z-50">
      <div className="sticky top-0 bg-white p-4 border-b border-gray-200 flex justify-between items-center">
        <h3 className="font-semibold text-gray-900">Kennisgewings</h3>
        <div className="flex gap-2">
          {notifications.some(n => !n.read) && (
            <Button
              data-testid="mark-all-read-btn"
              variant="ghost"
              size="sm"
              onClick={handleMarkAllRead}
            >
              <Check className="h-4 w-4 mr-1" />
              Merk alles
            </Button>
          )}
          {notifications.length > 0 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={handleClearAll}
              className="text-red-600 hover:text-red-700 hover:bg-red-50"
              title="Verwyder alle kennisgewings"
            >
              <Trash2 className="h-4 w-4 mr-1" />
              Maak skoon
            </Button>
          )}
          <Button variant="ghost" size="sm" onClick={onClose}>
            <X className="h-4 w-4" />
          </Button>
        </div>
      </div>

      <div className="p-2">
        {notifications.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            Geen kennisgewings nie
          </div>
        ) : (
          notifications.map((notification) => {
            const config = notificationTypeConfig[notification.type] || notificationTypeConfig.info;
            return (
              <div
                key={notification.id}
                className={`p-3 mb-2 rounded-lg border ${config.bg} ${config.border} ${!notification.read ? 'font-semibold' : ''}`}
              >
                <div className="flex items-start gap-3">
                  <div className={`w-2 h-2 rounded-full mt-2 flex-shrink-0 ${config.dot}`}></div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-gray-900 break-words">{notification.message}</p>
                    <p className="text-xs text-gray-500 mt-1">
                      {new Date(notification.created_date).toLocaleString('af-ZA')}
                    </p>
                  </div>
                  {!notification.read && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleMarkRead(notification.id)}
                      className="flex-shrink-0"
                    >
                      <Check className="h-3 w-3" />
                    </Button>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default NotificationPanel;